import { ListenerDictionary } from '../components/video-player/video-player.events'
import { AuthenticatedStreamResponse } from '@flocasts/flosports30-types/dist/live-api'
import { EnvironmentService } from '../../singleton-services/environment.service'
import { maybe } from 'typescript-monads'
import { IVideoLoggingContext, LogEventType, VideoEventLoggingService } from '../services/video-event-logging.service'
import Hls from 'hls.js'

const extractResolution = (elm: HTMLVideoElement) => `${elm.videoWidth}x${elm.videoHeight}`
const extractFile = (meta: AuthenticatedStreamResponse) => `https://${meta.playlist.edge}${meta.playlist.path}`

interface PlayerMeta {
  readonly streamId: string
  readonly eventStatus: string
  readonly hlsInstance: Hls
}

const extractBaseContext = (
  meta: AuthenticatedStreamResponse,
  ws: Window,
  playerId: string,
  playerInstance: string,
  appVersion: string,
  streamId: string,
  eventStatus: string
): IVideoLoggingContext => {
  return {
    playerId,
    playerInstance,
    file: extractFile(meta),
    referer: ws?.window?.location?.href,
    appVersion,
    appName: 'FloSports WebApp 3.0', // TODO: automate/inject this
    pluginName: 'hls.js', // TODO: automate/inject this
    pluginVersion: '0.13.2', // TODO: automate/inject this
    streamId,
    eventStatus
  }
}

const extractUser = (vidElement: HTMLVideoElement, meta: AuthenticatedStreamResponse) => {
  return {
    id: meta.token.userId,
    resolution: extractResolution(vidElement)
  }
}

const getVersion = (str?: string) => str || 'unspecified'

export const livePlayerEventsLoggingFactory = (
  vels: VideoEventLoggingService,
  ws: Window,
  es: EnvironmentService
): ListenerDictionary<AuthenticatedStreamResponse, PlayerMeta> => {
  return {
    canplay: (_event, videoElement, videoInstanceId, videoGroupId, hlsLoaderMeta, meta, _emitErrorFn) => {
      vels.logReadyEvent(
        hlsLoaderMeta.token.id,
        {
          ...extractBaseContext(
            hlsLoaderMeta,
            ws,
            videoGroupId,
            videoInstanceId,
            getVersion(es.config.appRevision),
            meta.streamId,
            meta.eventStatus
          )
        },
        extractUser(videoElement, hlsLoaderMeta)
      )
    },
    play: (_event, videoElement, videoInstanceId, videoGroupId, hlsLoaderMeta, meta, _emitErrorFn) => {
      vels.logPlayEvent(
        hlsLoaderMeta.token.id,
        {
          ...extractBaseContext(
            hlsLoaderMeta,
            ws,
            videoGroupId,
            videoInstanceId,
            getVersion(es.config.appRevision),
            meta.streamId,
            meta.eventStatus
          ),
          message: undefined,
          value: {
            previous: undefined
          }
        },
        extractUser(videoElement, hlsLoaderMeta)
      )
    },
    pause: (_event, videoElement, videoInstanceId, videoGroupId, hlsLoaderMeta, meta, _emitErrorFn) => {
      vels.logPauseEvent(
        hlsLoaderMeta.token.id,
        {
          ...extractBaseContext(
            hlsLoaderMeta,
            ws,
            videoGroupId,
            videoInstanceId,
            getVersion(es.config.appRevision),
            meta.streamId,
            meta.eventStatus
          ),
          message: undefined,
          value: {
            previous: undefined
          }
        },
        extractUser(videoElement, hlsLoaderMeta)
      )
    },
    volumechange: (_event, videoElement, videoInstanceId, videoGroupId, hlsLoaderMeta, meta, _emitErrorFn) => {
      const normalized = videoElement.volume * 100
      vels.logVolumeEvent.next({
        tokenId: hlsLoaderMeta.token.id,
        context: {
          ...extractBaseContext(
            hlsLoaderMeta,
            ws,
            videoGroupId,
            videoInstanceId,
            getVersion(es.config.appRevision),
            meta.streamId,
            meta.eventStatus
          ),
          message: `Manually changed to ${normalized}`,
          value: {
            volume: normalized
          }
        },
        user: extractUser(videoElement, hlsLoaderMeta),
        logEventType: LogEventType.VOLUME
      })
    },
    mute: (_event, videoElement, videoInstanceId, videoGroupId, hlsLoaderMeta, meta, _emitErrorFn) => {
      vels.logMuteEvent(
        hlsLoaderMeta.token.id,
        {
          ...extractBaseContext(
            hlsLoaderMeta,
            ws,
            videoGroupId,
            videoInstanceId,
            getVersion(es.config.appRevision),
            meta.streamId,
            meta.eventStatus
          ),
          value: {
            mute: true
          }
        },
        extractUser(videoElement, hlsLoaderMeta)
      )
    },
    error: (event, videoElement, videoInstanceId, videoGroupId, hlsLoaderMeta, meta, _emitErrorFn) => {
      // TODO: this section of code should be able to handle
      // multiple error formats? Currently catching errors
      try {
        vels.logErrorEvent(
          hlsLoaderMeta.token.id,
          {
            ...extractBaseContext(
              hlsLoaderMeta,
              ws,
              videoGroupId,
              videoInstanceId,
              getVersion(es.config.appRevision),
              meta.streamId,
              meta.eventStatus
            ),
            message: event.detail.message,
            value: {
              code: event.detail.code
            }
          },
          extractUser(videoElement, hlsLoaderMeta)
        )
      } catch (err) {
        const videoElementError = maybe(videoElement.error)
          .map(err => ({ code: err.code, stringCode: Object.keys(MediaError)[err.code - 1], message: err.message }))
          .valueOr({ code: 0, stringCode: 'UNKNOWN_ERR', message: 'UNKNOWN_ERR' })

        vels.logErrorEvent(
          hlsLoaderMeta.token.id,
          {
            ...extractBaseContext(
              hlsLoaderMeta,
              ws,
              videoGroupId,
              videoInstanceId,
              getVersion(es.config.appRevision),
              meta.streamId,
              meta.eventStatus
            ),
            // tslint:disable-next-line: max-line-length
            message: `videoErr: ${safeStringify(videoElementError)} hlsLoaderMeta: ${safeStringify(
              hlsLoaderMeta
            )} event: ${safeStringify(event)} error: ${safeStringify(err)}`,
            value: {
              code: videoElementError.code
            }
          },
          extractUser(videoElement, hlsLoaderMeta)
        )
      }
    }
  }
}

export const safeStringify = (input: any): string => {
  if (typeof input === 'string') {
    return input
  }
  try {
    return JSON.stringify(input)
  } catch (error) {
    return 'unable to stringify input'
  }
}
